import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { History, Search, Plus, Pencil, Trash2, Send, Check, X, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ACCION_CONFIG = {
  CREAR: { color: 'bg-emerald-100 text-emerald-700', icon: Plus },
  ACTUALIZAR: { color: 'bg-blue-100 text-blue-700', icon: Pencil },
  ELIMINAR: { color: 'bg-red-100 text-red-700', icon: Trash2 },
  ENVIAR_APROBACION: { color: 'bg-purple-100 text-purple-700', icon: Send },
  APROBAR: { color: 'bg-emerald-100 text-emerald-700', icon: Check },
  RECHAZAR: { color: 'bg-red-100 text-red-700', icon: X },
};

export default function RegistroAcciones() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroModulo, setFiltroModulo] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroModulo) params.set('modulo', filtroModulo);
      if (search) params.set('usuario', search);
      params.set('limit', '200');
      const res = await axios.get(`${API}/registro-acciones?${params}`, { headers });
      setRegistros(res.data);
    } catch { }
    setLoading(false);
  }, [token, filtroModulo, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const modulos = [...new Set(registros.map(r => r.modulo))].sort();

  return (
    <div className="space-y-6" data-testid="registro-acciones">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Registro de Acciones</h1>
        <p className="text-sm text-slate-500 mt-0.5">Historial de acciones realizadas en el sistema</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por usuario..."
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20"
              data-testid="search-registro" />
          </div>
          <select value={filtroModulo} onChange={e => setFiltroModulo(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-modulo">
            <option value="">Todos los modulos</option>
            {modulos.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#a05e49]" /></div>
        ) : registros.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <History className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-medium">No hay acciones registradas</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {registros.map(r => {
              const cfg = ACCION_CONFIG[r.accion] || { color: 'bg-slate-100 text-slate-600', icon: History };
              const Icon = cfg.icon;
              return (
                <div key={r.id} className="flex items-start gap-3 px-5 py-3.5 hover:bg-slate-50/50 transition-colors" data-testid={`log-${r.id}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${cfg.color.split(' ')[0]}`}>
                    <Icon className={`w-3.5 h-3.5 ${cfg.color.split(' ')[1]}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">{r.usuario_nombre || 'Sistema'}</span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${cfg.color}`}>{r.accion}</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-500">{r.modulo}</span>
                      {r.usuario_rol && <span className="text-[10px] text-slate-400">({r.usuario_rol})</span>}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5 truncate">{r.detalle}</p>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0 whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
